// Destino de retorno tras login (deep link).
//
// ÚNICA PUERTA de validación: cualquier destino —venga de sessionStorage
// (editable desde DevTools), de location.state (estado interno de react-router)
// o de donde sea— pasa SIEMPRE por `esRutaInternaSegura` antes de navegar. No se
// "arregla" un destino sospechoso: ante la mínima duda se descarta y el llamante
// cae a /panel-inicio.
//
// (Este commit añade solo la validación. La persistencia en sessionStorage
//  —guardar/consumir/limpiar— llega en el commit siguiente sobre este módulo.)

// Rutas que NUNCA son un destino válido: mandar aquí tras login crea bucles
// (login → login) o rompe el flujo de recuperación de contraseña. En minúsculas
// porque la comparación normaliza el pathname.
const RUTAS_VETADAS = ['/login', '/cambiarpassword'];

/**
 * Acepta SOLO rutas internas relativas (mismo origen, sin escapar del sitio).
 * Rechaza open redirects: esquemas (http:, https:, javascript:, data:…),
 * protocolo relativo (//host), backslashes, caracteres de control y URLs
 * absolutas —incluso del propio dominio—. Rechaza también /login y el flujo de
 * recuperación para evitar bucles.
 *
 * @param {unknown} destino
 * @returns {boolean}
 */
export const esRutaInternaSegura = (destino) => {
  if (typeof destino !== 'string') return false;
  if (destino.length === 0 || destino.length > 2000) return false;

  // Debe empezar por UNA sola '/'. Esto ya descarta todos los esquemas
  // (http:, javascript:…) y las URLs absolutas: ninguna empieza por '/'.
  if (destino.charCodeAt(0) !== 47 /* '/' */) return false;

  // Segundo carácter '/' → protocolo relativo '//host'; '\' → '/\host', que los
  // navegadores normalizan a '//host'. Ambos escaparían del origen.
  const segundo = destino.charCodeAt(1);
  if (segundo === 47 /* '/' */ || segundo === 92 /* '\' */) return false;

  // Ningún backslash (los navegadores lo tratan como '/') ni caracteres de
  // control/espacios de bajo rango en ninguna posición: se usan para colar un
  // '//host' que el parser luego reconstruye.
  for (let i = 0; i < destino.length; i++) {
    const c = destino.charCodeAt(i);
    if (c === 92 /* '\' */ || c < 0x20 || c === 0x7f) return false;
  }

  // pathname sin query ni hash, en minúsculas, contra la lista de vetadas.
  const pathname = destino.split(/[?#]/)[0].toLowerCase();
  if (RUTAS_VETADAS.some((v) => pathname === v || pathname.startsWith(v + '/'))) {
    return false;
  }

  return true;
};

/**
 * Convierte el `from` de location.state (objeto de react-router
 * { pathname, search, hash } o ya una cadena) en una ruta interna, VALIDADA por
 * la misma puerta. Devuelve la cadena segura o null.
 *
 * @param {{pathname?: string, search?: string, hash?: string} | string | null | undefined} from
 * @returns {string | null}
 */
export const destinoDesdeLocationState = (from) => {
  if (!from) return null;
  const destino =
    typeof from === 'string'
      ? from
      : `${from.pathname || ''}${from.search || ''}${from.hash || ''}`;
  return esRutaInternaSegura(destino) ? destino : null;
};

// ---------------------------------------------------------------------------
// Persistencia del destino pendiente.
//
// Se guarda en sessionStorage (por pestaña) y NO en location.state, que no
// sobrevive a ninguno de los tres bordes donde hoy se pierde el deep link:
// el reload por desajuste de versión, el round-trip del reset de contraseña y
// el navigate("/login") seco del 401. Se marca con timestamp y caduca: un
// intento abandonado no debe redirigir media hora después.
// ---------------------------------------------------------------------------

const CLAVE = 'intranet.destinoRetorno';
const TTL_MS = 30 * 60 * 1000; // 30 min

/** Ruta actual completa (pathname+search+hash) del navegador. */
export const rutaActual = () =>
  `${window.location.pathname}${window.location.search}${window.location.hash}`;

/**
 * Guarda un destino pendiente si —y solo si— es una ruta interna segura. No
 * persiste basura: validar al escribir evita almacenar lo que igualmente se
 * rechazaría al leer.
 * @param {string} destino
 */
export const guardarDestinoRetorno = (destino) => {
  if (!esRutaInternaSegura(destino)) return;
  try {
    sessionStorage.setItem(CLAVE, JSON.stringify({ destino, ts: Date.now() }));
  } catch {
    // sessionStorage lleno/bloqueado (modo privado, etc.): no es crítico.
  }
};

/**
 * Lee el destino pendiente, lo BORRA (consumir de un solo uso: si se quedara,
 * un login posterior saltaría a un sitio que el usuario ya no espera) y lo
 * valida —caducidad + misma puerta de seguridad, porque sessionStorage es
 * editable desde DevTools—. Devuelve la ruta segura o null.
 * @returns {string | null}
 */
export const consumirDestinoRetorno = () => {
  let crudo;
  try {
    crudo = sessionStorage.getItem(CLAVE);
  } catch {
    return null;
  }
  if (!crudo) return null;
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    // si no se puede borrar, seguimos: la caducidad acota el daño.
  }
  let dato;
  try {
    dato = JSON.parse(crudo);
  } catch {
    return null;
  }
  if (!dato || typeof dato.ts !== 'number') return null;
  if (Date.now() - dato.ts > TTL_MS) return null; // intento caducado
  return esRutaInternaSegura(dato.destino) ? dato.destino : null;
};

/**
 * Borra cualquier destino pendiente. Se llama en el logout explícito: cerrar
 * sesión a propósito no debe dejar un destino esperando a resucitar en el
 * siguiente login.
 */
export const limpiarDestinoRetorno = () => {
  try {
    sessionStorage.removeItem(CLAVE);
  } catch {
    // nada que limpiar / no accesible.
  }
};

// ---------------------------------------------------------------------------
// Fase A1 · vuelta tras configurar el segundo factor.
//
// Quien se encuentra la puerta —en Soporte, en el listado, o en RRHH cuando
// llegue— y no tiene el 2FA configurado, se va al perfil a ponérselo. Al
// terminar debe volver DONDE ESTABA, no quedarse en el perfil teniendo que
// recordar qué iba a hacer.
//
// CLAVE PROPIA, distinta de la del login. Compartirla haría que un retorno se
// comiera el otro: alguien que va a configurarse el 2FA y de paso caduca la
// sesión acabaría en el sitio equivocado. Misma puerta de validación, mismo
// consumo de un solo uso, misma caducidad: lo único que cambia es el cajón.
// ---------------------------------------------------------------------------

const CLAVE_2FA = 'intranet.destinoTras2FA';

/**
 * Guarda dónde estaba quien se va a configurar el segundo factor.
 * @param {string} destino  normalmente `rutaActual()`
 */
export const guardarDestinoTras2FA = (destino) => {
  if (!esRutaInternaSegura(destino)) return;
  try {
    sessionStorage.setItem(CLAVE_2FA, JSON.stringify({ destino, ts: Date.now() }));
  } catch {
    // sessionStorage lleno o bloqueado: se pierde la vuelta, no el flujo.
  }
};

/**
 * Lee y BORRA el destino. Se llama SOLO cuando la configuración ha terminado
 * de verdad: si alguien la abandona a medias, volver a la ficha le dejaría otra
 * vez la invitación delante sin entender por qué, así que en ese caso se queda
 * donde está y el destino caduca solo a los 30 minutos.
 * @returns {string | null}
 */
export const consumirDestinoTras2FA = () => {
  let crudo;
  try {
    crudo = sessionStorage.getItem(CLAVE_2FA);
  } catch {
    return null;
  }
  if (!crudo) return null;
  try {
    sessionStorage.removeItem(CLAVE_2FA);
  } catch {
    // la caducidad acota el daño si no se puede borrar.
  }
  let dato;
  try {
    dato = JSON.parse(crudo);
  } catch {
    return null;
  }
  if (!dato || typeof dato.ts !== 'number') return null;
  if (Date.now() - dato.ts > TTL_MS) return null;
  return esRutaInternaSegura(dato.destino) ? dato.destino : null;
};
